import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { isVaultableRarity } from '@sorare/core/src/lib/cards';

import { SealingOnboardingBanner } from '#src/components/sealing/SealingOnboardingBanner/index.tsx';

import type {
  RarityVaultBannerQuery,
  RarityVaultBannerQueryVariables,
} from './__generated__/index.withFetch.graphql.ts';
import type { Props } from './index.tsx';
import { RarityVaultBanner } from './index.tsx';

const query = gql`
  query RarityVaultBannerQuery($rarity: Rarity!, $sport: Sport!) {
    config {
      id
      sealingConfig(sport: $sport) {
        ...RarityVaultBanner_sealingConfig
      }
    }
    currentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        rarityVaultBoost(rarity: $rarity, sport: $sport) {
          points
          ...RarityVaultBanner_rewardBoost
        }
      }
    }
  }
  ${RarityVaultBanner.fragments.sealingConfig}
  ${RarityVaultBanner.fragments.rewardBoost}
` as TypedDocumentNode<RarityVaultBannerQuery, RarityVaultBannerQueryVariables>;

export const RarityVaultBannerWithFetch = ({
  additionalPoints = 0,
  recap = false,
  hideEmptyVault = false,
  showSealingOnboardingWhenApplicable = false,
  ...props
}: Pick<
  Props,
  | 'rarity'
  | 'additionalPoints'
  | 'minimumPointsRequired'
  | 'isBoostLevelRequirementBanner'
  | 'showHowItWorksCta'
  | 'withBackground'
> & {
  recap?: boolean;
  hideEmptyVault?: boolean;
  showSealingOnboardingWhenApplicable?: boolean;
}) => {
  const { sport } = useSportContext();
  const { lifecycle } = useLifecycle();
  const { data, loading } = useQuery(query, {
    variables: { rarity: props.rarity, sport },
    skip: !isVaultableRarity(props.rarity),
  });

  const points =
    data?.currentUser?.anyUserSportProfile?.rarityVaultBoost.points || 0;

  if (hideEmptyVault && points === 0) {
    return null;
  }

  const hasDismissedSealingOnboarding =
    lifecycle?.[LIFECYCLE.sawMlbSealingOnboarding] === true;

  if (showSealingOnboardingWhenApplicable && points === 0) {
    if (!hasDismissedSealingOnboarding) {
      return (
        <Skeleton loading={loading}>
          <SealingOnboardingBanner />
        </Skeleton>
      );
    }
    return null;
  }

  const fullRarityVaultMatrix =
    data?.config?.sealingConfig?.fullRarityVaultMatrix;

  return (
    <RarityVaultBanner
      {...props}
      points={recap ? points - additionalPoints : points}
      fullRarityVaultMatrix={fullRarityVaultMatrix}
      additionalPoints={additionalPoints}
    />
  );
};
